// Registry of client-side HTML fragments (the former views/*.tpl), keyed two ways:
//
//   tabViews[tpl]   the tab body a ui.cfg state names in its "tpl" field. When
//                   present, the SPA renders it instead of fetching the legacy
//                   /tab?index=N fragment from the server.
//   views[name]     fragments a WB.* proc renders itself through WB.views.get /
//                   WB.views.render, replacing a $.get("<start route>") that
//                   returned HTML. `static` views need nothing from the server;
//                   the others call their route for JSON and render with it.
//
// Converting a tab = move its .tpl here (tools/spa_convert_tpl.py), make its
// start route return JSON (or drop it), and change the proc's $.get to
// WB.views.get. Progress is tracked in the wb repo, docs/SPA-PLAN.md.
import { render, mount } from '../core/tpl.js';
import { T } from '../core/i18n.js';

import tabdisplay from './tabdisplay.html?raw';
import shipping from './shipping.html?raw';
import batch_quoterreports from './batch_quoterreports.html?raw';
import batchtab from './batchtab.html?raw';
import batchdevicetab from './batchdevicetab.html?raw';
import batch_monthlyreport from './batch_monthlyreport.html?raw';
import ship_list from './ship_list.html?raw';
import repairtab from './repairtab.html?raw';
import quicktab from './quicktab.html?raw';
import proshiptab from './proshiptab.html?raw';
import tagstab from './tagstab.html?raw';
import lotidsrpt from './lotidsrpt.html?raw';
import inventorytab from './inventorytab.html?raw';
import customertab from './customertab.html?raw';
import billreport from './billreport.html?raw';
import invoicereport from './invoicereport.html?raw';
import reconciliationreport from './reconciliationreport.html?raw';
import commercialinvoicereport from './commercialinvoicereport.html?raw';
import adjustmentreport from './adjustmentreport.html?raw';
import pradjustmentreport from './pradjustmentreport.html?raw';
import seller_listings_snapshot from './seller_listings_snapshot.html?raw';
import product_listings_snapshot from './product_listings_snapshot.html?raw';
import tracking from './tracking.html?raw';
import inprogress_list from './inprogress_list.html?raw';
import known_quality from './known_quality.html?raw';
import warehouse from './warehouse.html?raw';
import transmogrify from './transmogrify.html?raw';
import lots from './lots.html?raw';
import ticket_admin_list from './ticket_admin_list.html?raw';
import preownedplaceholder from './preownedplaceholder.html?raw';
import preownedsourcing from './preownedsourcing.html?raw';
import preownedcreateorder from './preownedcreateorder.html?raw';
import customer_list from './customer_list.html?raw';
import eztrade from './eztrade.html?raw';
import listings from './listings.html?raw';
import upcominginventory from './upcominginventory.html?raw';
// admin lists (Locations, Users, Roles, Groups, PK map, Machine IDs)
import location_list from './location_list.html?raw';
import user_list from './user_list.html?raw';
import role_list from './role_list.html?raw';
import group_list from './group_list.html?raw';
import pkmap_list from './pkmap_list.html?raw';
import machid_list from './machid_list.html?raw';
import ticket_list from './ticket_list.html?raw';
import purchaser_ticket_list from './purchaser_ticket_list.html?raw';
// tab bodies named by ui.cfg states ("tpl")
import devicetab from './devicetab.html?raw';
import quotetab from './quotetab.html?raw';
import retail_overview from './retail_overview.html?raw';
import purchaser_overview from './purchaser_overview.html?raw';
import pricedata from './pricedata.html?raw';
import b2b_overview from './b2b_overview.html?raw';
import webactivity from './webactivity.html?raw';
import commercialinvoice from './commercialinvoice.html?raw';
import adjustmentlist from './adjustmentlist.html?raw';
import yourpricetab from './yourpricetab.html?raw';
import yourrepairpricetab from './yourrepairpricetab.html?raw';
import quote_feedback_overview from './quote_feedback_overview.html?raw';
import lotsimport from './lotsimport.html?raw';
import transmogrify_list from './transmogrify_list.html?raw';
import proship_batchcustomer from './proship_batchcustomer.html?raw';

export const tabViews = {
  tabdisplay,
  devicetab,
  quotetab,
  retail_overview,
  purchaser_overview,
  pricedata,
  b2b_overview,
  webactivity,
  commercialinvoice,
  adjustmentlist,
  yourpricetab,
  yourrepairpricetab,
  quote_feedback_overview,
  lotsimport,
  transmogrify_list,
  // ui.cfg names this one with its extension
  'proship_batchcustomer.tpl': proship_batchcustomer,
  proship_batchcustomer,
  // still server-rendered (workflow-style, they carry a WBstate and commands.tpl):
  // import, adminstart, wfdisplay
};

// The report picker: which reports a role sees and which opens first. This was
// the %if chain at the top of batch_quoterreports.tpl, kept in the same order.
function reportsPrepare(ctx) {
  const role = ctx.role;
  const primary = !!ctx.IsPrimary;
  const is = (...roles) => roles.indexOf(role) >= 0;
  let defaultReport;
  if (is('repair_manager', 'repair_associate')) defaultReport = T('Repair Report');
  else if (is('managed_service_retail_manager', 'ezTrade_manager', 'ezTrade_orgadmin')) defaultReport = T('ezTrade Report');
  else if (is('customer', 'batch_customer')) defaultReport = T('Pro Shipping Report');
  else if (primary && role === 'cellbie_sales') defaultReport = T('Pro Shipping Report');
  else if (role === 'Pro_overview') defaultReport = T('Pro Shipping Report');
  else if (role === 'ms_ci_creator') defaultReport = T('Commercial Invoice Report');
  else defaultReport = T('Device Report');

  const items = [];
  const add = (...names) => names.forEach((n) => items.push(T(n)));
  if (is('managed_service_retail_manager', 'ezTrade_manager', 'ezTrade_orgadmin')) {
    add('ezTrade Report');
  } else if (role === 'Pro_overview') {
    add('Pro Shipping Report');
  } else if (role === 'ms_ci_creator' && ctx.doCIreport) {
    add('Commercial Invoice Report');
  } else {
    if (is('quoter', 'bulk_seller', 'B2B_Supervisor', 'B2B', 'quoter_plus', 'B2B+', 'Pro_B2B')) add('Reconciliation Report', 'Adjustment Report');
    if (ctx.hasSellerListingsSnapshot) add('Seller Listings Snapshot');
    if (ctx.hasProductListingsSnapshot) add('Product Listings Snapshot');
    if (is('managed_service_org_admin', 'org_admin', 'business_ops_admin', 'managed_service_quoter', 'ms_B2B', 'bulk_seller', 'B2B_Supervisor', 'B2B', 'B2B+', 'Pro_B2B')) add('ezTrade Report');
    if (is('repair_manager', 'org_admin', 'business_ops_admin')) add('Repair Report');
    if (is('retail_manager', 'org_admin', 'business_ops_admin')) add('Reconciliation Report');
    if (is('device_purchaser', 'org_admin', 'business_ops_admin')) add('Inventory Integration Report', 'Adjustment Report');
    if (is('customer', 'batch_customer', 'managed_service_quoter', 'ms_B2B', 'Pro_B2B', 'bulk_seller', 'B2B_Supervisor', 'B2B', 'B2B+', 'retail_manager', 'org_admin', 'business_ops_admin')) add('Pro Shipping Report');
    if (is('ms_B2B', 'business_ops_admin', 'bulk_seller', 'B2B_Supervisor', 'B2B', 'B2B+')) add('Tag Report', 'LotID Report');
    if (role === 'device_purchaser' && ctx.doCIreport) add('Commercial Invoice Report');
    if (primary && role === 'cellbie_sales') {
      add('Pro Shipping Report', 'Inventory Integration Report', 'Device Report', 'Customer Report', 'Reconciliation Report',
        'Commercial Invoice Report', 'LotID Report', 'Billing Report', 'Adjustment Report', 'Post Rcv Adjustment Report');
    } else if (role === 'repair_associate') {
      add('Repair Report');
    } else {
      add('Device Report');
    }
    if (primary && role === 'org_admin') add('Billing Report');
    if (primary && role === 'cellbie_sales') add('Invoice Report');
  }
  return { defaultReport, reportItems: items };
}

export const views = {
  shipping: { html: shipping, static: true },
  // Reports tab and its report panels
  // flags come from session.features; startquoterreports is no longer called
  batch_quoterreports: { html: batch_quoterreports, prepare: reportsPrepare, static: true },
  batchtab: { html: batchtab, static: true },
  batchdevicetab: { html: batchdevicetab, static: true },
  batch_monthlyreport: { html: batch_monthlyreport, static: true },
  ship_list: { html: ship_list, static: true },
  repairtab: { html: repairtab, static: true },
  quicktab: { html: quicktab, static: true },
  proshiptab: { html: proshiptab, static: true },
  tagstab: { html: tagstab, static: true },
  lotidsrpt: { html: lotidsrpt, static: true },
  pradjustmentreport: { html: pradjustmentreport, static: true },
  seller_listings_snapshot: { html: seller_listings_snapshot, static: true },
  inventorytab: { html: inventorytab },
  customertab: { html: customertab },
  billreport: { html: billreport },
  invoicereport: { html: invoicereport },
  reconciliationreport: { html: reconciliationreport, static: true },
  commercialinvoicereport: { html: commercialinvoicereport },
  adjustmentreport: { html: adjustmentreport, static: true }, // hostmode from session.features
  product_listings_snapshot: { html: product_listings_snapshot },
  // Tracking tab: the prompt starttracking chose by role, from the session's tracking* features
  tracking: {
    html: tracking,
    static: true,
    prepare: (ctx) => {
      let prompt;
      if (ctx.trackingSeeOutbound && ctx.trackingSeeInbound && ctx.isUber) prompt = T('You are seeing all active tracking numbers from all domains on the system.');
      else if (ctx.trackingSeeOutbound && ctx.trackingSeeInbound) prompt = T('You are seeing all active tracking numbers, inbound and outbound, from your domain.');
      else if (ctx.trackingSeeInbound) prompt = T('You are seeing all tracking numbers inbound to your domain.');
      else if (ctx.trackingLocationOnly) prompt = T('You are seeing all tracking numbers outbound from your location(s).');
      else prompt = T('You are seeing all tracking numbers outbound from your domain.');
      return { prompt };
    },
  },
  // In Process (B2B and retail flavours share the fragment; retail comes back in the data)
  inprogress_list: { html: inprogress_list },
  // Known Quality
  known_quality: {
    html: known_quality,
    prepare: (ctx) => ({ showMceTestMule: !!ctx.mcews && ctx.role === 'B2B+' }),
  },
  // Warehouse receiving, Transmogrifier dialogs
  warehouse: { html: warehouse, static: true },
  transmogrify: { html: transmogrify, static: true },
  transmogrify_list: { html: transmogrify_list, static: true },
  // Lots (buyer, creator and Cellbie flavours share the fragment; flags come back in the data)
  lots: { html: lots, static: true }, // lotcreator/lotbuyer/cellbiesales from session.features
  // Admin tickets
  ticket_admin_list: { html: ticket_admin_list },
  // Pre-owned sourcing (Sourcing / Create Order / Pending)
  preownedplaceholder: { html: preownedplaceholder, static: true }, // gate: session.features.pendingTabVisible
  preownedsourcing: { html: preownedsourcing },
  preownedcreateorder: { html: preownedcreateorder },
  // Customers / Clients
  customer_list: {
    html: customer_list,
    prepare: (ctx) => ({
      customerCount: (ctx.customer_info || []).length,
      tableClass: ctx.labels && ctx.labels.manageUsers ? 'fixedHeaderCol3' : 'fixedHeaderCol2',
    }),
  },
  // ezTrade
  eztrade: { html: eztrade, static: true }, // showquote from session.features
  // Listings and Live Inventory
  listings: {
    html: listings,
    static: true, // allowed_qualities/listings_disabled from session.features; gate: listingsVisible
    prepare: (ctx) => {
      const allowed = ctx.allowed_qualities && ctx.allowed_qualities.length ? ctx.allowed_qualities : ['A', 'B', 'C', 'D', 'E', 'R'];
      const qualities = {};
      allowed.forEach((q) => {
        qualities[q] = true;
      });
      return { qualities };
    },
  },
  upcominginventory: { html: upcominginventory, static: true },
  // admin lists
  location_list: {
    html: location_list,
    prepare: (ctx) => ({
      // roles whose Locations screen keeps the side form on the same row (see wfprocs doLocationList)
      locationUiChangeRequired: ['ezTrade_manager', 'repair_estimate_manager', 'repair_manager', 'retail_manager', 'device_purchaser'].indexOf(ctx.role) >= 0,
    }),
  },
  user_list: { html: user_list },
  role_list: { html: role_list },
  group_list: { html: group_list },
  pkmap_list: { html: pkmap_list },
  machid_list: { html: machid_list },
  // admin Tickets tab (rows and per-row decisions come from /listtickets)
  ticket_list: { html: ticket_list },
  // purchaser view of inbound tickets (rows from /ui/purchasertickets)
  purchaser_ticket_list: {
    html: purchaser_ticket_list,
    prepare: (ctx) => ({ devicesText: T('{0} devices.').replace('{0}', String(ctx.numdevices || 0)) }),
  },
};

// Procs whose own route records the tab visit (the WEBUI log the legacy /tab
// wrote). None today: every client-rendered tab posts /domenu with its title.
export const PROCS_LOGGING_THEIR_VISIT = new Set();

// What every fragment can see: the session plus the names the templates used
// (`userlevel`, `IsPrimary`, ...) and a few booleans standing in for the
// expressions Bottle templates evaluated inline.
function baseContext(WB) {
  const sess = WB.session || {};
  const user = sess.user || {};
  const org = sess.org || {};
  const role = user.role || '';
  const level = Number(user.level || 0);
  const lang = sess.lang || 'en-ca';
  const country = org.country || '';
  const roleIs = {};
  if (role) roleIs[role] = true;
  // Per-tenant / per-role flags from /ui/session (doCIreport, hostmode, showquote,
  // lotbuyer, allowed_qualities, mcews, ...). Spread first so a start* route's own
  // payload, when a view still fetches one, overrides with the same names.
  return Object.assign({}, sess, sess.features || {}, {
    role,
    roleIs, // {{#if roleIs.cellbie_sales}}
    userrole: role,
    userlevel: level,
    IsPrimary: !!org.isPrimary,
    isUber: !!sess.isCellbieAdmin,
    domainCountryCode: country,
    managerLevel: level >= 60,
    adminLevel: level >= 80,
    notRetailManager: role !== 'retail_manager',
    primarySales: !!org.isPrimary && role === 'cellbie_sales',
    isCustomerRole: role === 'customer' || role === 'batch_customer',
    isB2BRole: ['B2B', 'B2B+', 'Pro_B2B', 'B2B_Supervisor'].indexOf(role) >= 0,
    isQuoterPlusRole: role === 'quoter_plus' || role === 'B2B+',
    isOverviewAdmin: ['org_admin', 'cellbie_sales', 'business_ops_admin'].indexOf(role) >= 0,
    // the device-conditions help page, per language and country (devicetab, quotetab)
    conditionsHelpFile: '/static/resources/' + lang + (country === 'us' ? '/conditions_5_column_us.html' : '/conditions_5_column.html'),
  });
}

function contextFor(WB, view, data) {
  let ctx = Object.assign(baseContext(WB), data || {});
  if (view.prepare) ctx = Object.assign(ctx, view.prepare(ctx) || {});
  return ctx;
}

export function installViewApi(WB) {
  const $ = window.$;
  const lookup = (name) => {
    const v = views[name];
    if (!v) throw new Error('unknown view ' + name);
    return typeof v === 'string' ? { html: v, static: true } : v;
  };
  WB.views = {
    has: (name) => Object.prototype.hasOwnProperty.call(views, name),
    isStatic: (name) => !!lookup(name).static,
    render: (name, data) => {
      const v = lookup(name);
      return render(v.html, contextFor(WB, v, data));
    },
    mount: (el, name, data) => {
      const v = lookup(name);
      return mount(el, v.html, contextFor(WB, v, data));
    },
    // Drop-in for `$.get(url, function (html) {...})`: static views render at
    // once with no request; the rest fetch `url` as JSON and render with it.
    // The callback gets (html, data) so callers can use the payload too. Returns
    // a small handle with .fail()/.done() for the callers that chained those.
    get: (name, url, callback) => {
      const v = lookup(name);
      const failHandlers = [];
      const doneHandlers = [];
      const handle = {
        fail: (fn) => {
          failHandlers.push(fn);
          return handle;
        },
        done: (fn) => {
          doneHandlers.push(fn);
          return handle;
        },
      };
      const finish = (html, data) => {
        callback(html, data);
        doneHandlers.forEach((fn) => fn(html, data));
      };
      if (v.static) {
        setTimeout(() => finish(render(v.html, contextFor(WB, v, null)), null), 0);
        return handle;
      }
      $.ajax({
        url,
        dataType: 'json',
        cache: false,
        success: (data) => {
          if (data && data.ok === false) {
            window.bootbox.alert(data.msg || T('Request failed.'));
            failHandlers.forEach((fn) => fn(null, 'error', data.msg));
            return;
          }
          finish(render(v.html, contextFor(WB, v, data)), data);
        },
        error: function (xhr, status, err) {
          if (failHandlers.length) failHandlers.forEach((fn) => fn(xhr, status, err));
          else WB.handleJSONError(xhr);
        },
      });
      return handle;
    },
  };
}
